<template>
  <div
    class="task-card"
    :class="{
      'task-card--completed': value.completedAt,
      'task-card--active': isActive(),
    }"
  >
    <!-- Header: checkbox + title -->
    <div class="task-card__header">
      <button
        class="task-card__check"
        :class="{ 'task-card__check--done': value.completedAt }"
        @click.stop="toggleComplete"
        @mousedown.stop
        :title="value.completedAt ? 'Mark as incomplete' : 'Mark as complete'"
      >
        <svg v-if="value.completedAt" viewBox="0 0 12 12" fill="none">
          <path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <span class="task-card__title" :class="{ 'task-card__title--done': value.completedAt }">
        {{ getFirstLineFromHtml(value.description) }}
      </span>
    </div>

    <!-- Metadata badges -->
    <div class="task-card__meta" v-if="isActive() || value.deadline || totalTime || value.plannedDurationHours || checkboxStatus">
      <span v-if="isActive()" class="task-card__badge task-card__badge--running">
        <span class="task-card__pulse"></span>Running
      </span>
      <span v-if="value.deadline" class="task-card__badge" :class="deadlineBadgeClass">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" style="width:10px;height:10px;flex-shrink:0;">
          <rect x="2" y="3" width="12" height="11" rx="1.5"/><path d="M5 1v2M11 1v2M2 7h12"/>
        </svg>
        {{ formatDeadline(value.deadline) }}
      </span>
      <span v-if="totalTime || value.plannedDurationHours" class="task-card__badge task-card__badge--time" :class="timeBadgeClass">
        <svg viewBox="0 0 16 16" fill="none" style="width:11px;height:11px;flex-shrink:0;">
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.5"/>
          <path d="M8 5v3.5l2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        {{ totalTime || 0 }}h<span v-if="value.plannedDurationHours" style="opacity:0.55;"> / {{ value.plannedDurationHours }}h</span>
      </span>
      <span v-if="checkboxStatus" class="task-card__badge task-card__badge--check">
        ☑ {{ checkboxStatus }}
      </span>
    </div>

    <!-- Actions -->
    <div class="task-card__actions">
      <template v-if="!value.completedAt">
        <IconBtn
          v-if="!loading.timer"
          :variant="isActive() ? 'timer-on' : undefined"
          @click.stop="isActive() ? stopEntry() : createEntry()"
          @mousedown.stop
          :title="isActive() ? 'Stop timer' : 'Start timer'"
        >
          <svg v-if="!isActive()" viewBox="0 0 16 16" fill="currentColor"><polygon points="4,2 14,8 4,14"/></svg>
          <svg v-else viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="3" width="4" height="10" rx="1"/><rect x="9" y="3" width="4" height="10" rx="1"/></svg>
        </IconBtn>
        <span v-else class="task-card__loading"><Loading size="13" :dark="true"/></span>
      </template>

      <IconBtn @click.stop="$emit('edit')" @mousedown.stop title="Edit task">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M11.5 2.5a1.414 1.414 0 012 2L5 13l-3 1 1-3 8.5-8.5z"/>
        </svg>
      </IconBtn>

      <IconBtn
        v-if="value.completedAt && !value.archivedAt"
        @click.stop="$emit('archive')"
        @mousedown.stop
        title="Archive task"
      >
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M14 5.5v9H2v-9M1 2h14v3.5H1zM6.5 8h3"/>
        </svg>
      </IconBtn>

      <IconBtn
        v-if="githubUrl"
        @click.stop="openUrl(githubUrl)"
        @mousedown.stop
        title="Open GitHub link"
      >
        <img src="../../assets/github-black.png" alt="GitHub" style="width:13px;height:13px;opacity:0.45;display:block;" />
      </IconBtn>

      <IconBtn variant="danger" @click.stop="$emit('delete')" @mousedown.stop title="Delete task">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 4h12M6 4V2.5h4V4M13 4l-.8 9.5H3.8L3 4"/>
        </svg>
      </IconBtn>
    </div>
  </div>
</template>

<script>
  import moment from 'moment'
  import Loading from '../common/Loading.vue'
  import IconBtn from '../common/IconBtn.vue'
  import {
    getFirstLineFromHtml,
    durationAsHours,
    countUncheckedFromHtml,
    countCheckedFromHtml
  } from '@/utils'

  export default {
    components: { Loading, IconBtn },
    name: 'TaskEntry',
    props: ['value'],
    emits: ['edit', 'complete', 'resetComplete', 'archive', 'delete'],
    data: () => ({
      loading: { timer: false }
    }),
    computed: {
      activeTimeEntries() {
        return this.$store.getters.activeTimeEntries
      },
      descriptionUrls() {
        const links = []
        if (this.value.description.indexOf('<a') > -1) {
          const preLinks = this.value.description.split('href="')
          for (let i = 1; i < preLinks.length; i++) {
            links.push(preLinks[i].split('"')[0])
          }
        }
        return links
      },
      githubUrl() {
        return this.descriptionUrls.find(url => url.indexOf('github.com') > -1)
      },
      totalTime() {
        const entries = this.$store.getters.timeEntries.filter(
          e => e.taskId === this.value.id
        )
        let hours = 0
        for (const entry of entries) {
          hours += durationAsHours(entry.start, entry.end ? entry.end : moment().format())
        }
        return parseFloat(Math.round(hours * 100) / 100) || 0
      },
      timeSpentRatio() {
        return this.value.plannedDurationHours && this.totalTime
          ? this.totalTime / this.value.plannedDurationHours
          : 0
      },
      timeBadgeClass() {
        if (!this.timeSpentRatio) return ''
        if (this.timeSpentRatio > 1) return 'task-card__badge--over'
        if (this.timeSpentRatio >= 0.8) return 'task-card__badge--warn'
        return ''
      },
      deadlineBadgeClass() {
        if (moment(this.value.deadline).add(-2, 'days').isBefore(moment()))
          return 'task-card__badge--urgent'
        if (moment(this.value.deadline).add(-7, 'days').isBefore(moment()))
          return 'task-card__badge--soon'
        return 'task-card__badge--ok'
      },
      uncheckedCheckboxes() {
        return countUncheckedFromHtml(this.value.description)
      },
      checkedCheckboxes() {
        return countCheckedFromHtml(this.value.description)
      },
      checkboxStatus() {
        if (!this.uncheckedCheckboxes && !this.checkedCheckboxes) return ''
        return `${this.checkedCheckboxes} / ${this.uncheckedCheckboxes + this.checkedCheckboxes}`
      }
    },
    methods: {
      getFirstLineFromHtml,
      isActive() {
        return !!this.activeTimeEntries.find(e => e.taskId === this.value.id)
      },
      getActiveTimeEntry() {
        return this.activeTimeEntries.find(e => e.taskId === this.value.id)
      },
      async createEntry() {
        this.loading.timer = true
        await this.$store.dispatch('createTimeEntry', {
          description: this.value.description,
          projectId: this.value.projectId,
          start: moment().format(),
          taskId: this.value.id
        })
        this.loading.timer = false
      },
      async stopEntry() {
        this.loading.timer = true
        const entry = this.getActiveTimeEntry()
        await this.$store.dispatch('updateTimeEntry', {
          id: entry.id,
          end: moment().format()
        })
        this.loading.timer = false
      },
      toggleComplete() {
        if (this.value.completedAt) {
          this.$emit('resetComplete')
        } else {
          this.$emit('complete')
        }
      },
      formatDeadline(date) {
        const m = moment(date)
        if (m.isSame(moment(), 'year')) return m.format('MMM D')
        return m.format('MMM D, YYYY')
      },
      openUrl(url) {
        window.ipc.send('openUrl', url)
      }
    }
  }
</script>

<style scoped>
.task-card {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 12px 14px 10px;
  cursor: grab;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  user-select: none;
}

.task-card:hover {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 0, 0, 0.13);
}

.task-card--active {
  border-color: rgba(61, 106, 144, 0.35);
  box-shadow: 0 0 0 3px var(--primary-muted);
}

.task-card--completed {
  background: rgba(0, 0, 0, 0.02);
}

/* Header */
.task-card__header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}

.task-card__check {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid rgba(0, 0, 0, 0.2);
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin-top: 1px;
  transition: border-color 0.15s, background 0.15s;
  color: white;
}

.task-card__check:hover {
  border-color: var(--primary);
}

.task-card__check--done {
  background: var(--primary);
  border-color: var(--primary);
}

.task-card__title {
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.8);
  line-height: 1.4;
  word-break: break-word;
}

.task-card__title--done {
  text-decoration: line-through;
  color: rgba(0, 0, 0, 0.35);
}

/* Meta */
.task-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 10px;
  padding-left: 28px;
}

.task-card__badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 2px 7px;
  border-radius: 100px;
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.55);
}

.task-card__badge--running {
  background: rgba(61, 144, 80, 0.1);
  color: #2e7d45;
}

.task-card__badge--ok {
  background: var(--primary-muted);
  color: var(--primary);
}

.task-card__badge--soon {
  background: rgba(180, 120, 40, 0.12);
  color: #b87328;
}

.task-card__badge--urgent {
  background: rgba(190, 60, 60, 0.1);
  color: #b03a3a;
}

.task-card__badge--time {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.5);
}

.task-card__badge--warn {
  background: rgba(180, 120, 40, 0.12);
  color: #b87328;
}

.task-card__badge--over {
  background: rgba(190, 60, 60, 0.1);
  color: #b03a3a;
}

/* Pulse animation */
.task-card__pulse {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2e7d45;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.75); }
}

/* Actions */
.task-card__actions {
  display: flex;
  align-items: center;
  gap: 2px;
  padding-left: 26px;
}

.task-card__loading {
  cursor: default;
}
</style>
